/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CreateTextMessageTemplateRequest } from '../models/create-text-message-template-request';
import { RenderTextMessageTemplateRequest } from '../models/render-text-message-template-request';
import { RenderedTextMessageContent } from '../models/rendered-text-message-content';
import { TextMessageTemplate } from '../models/text-message-template';
import { UpdateTextMessageTemplateRequest } from '../models/update-text-message-template-request';


/**
 * API for managing text message templates.
 */
@Injectable({
  providedIn: 'root',
})
export class TextMessageTemplatesService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation listTextMessageTemplates
   */
  static readonly ListTextMessageTemplatesPath = '/text-message-templates';

  /**
   * List all text message templates.
   *
   * Returns a list of all available text message templates.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listTextMessageTemplates()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTextMessageTemplates$Response(params?: {
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<TextMessageTemplate>>> {

    const rb = new RequestBuilder(this.rootUrl, TextMessageTemplatesService.ListTextMessageTemplatesPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<TextMessageTemplate>>;
      })
    );
  }

  /**
   * List all text message templates.
   *
   * Returns a list of all available text message templates.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listTextMessageTemplates$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTextMessageTemplates(params?: {
    context?: HttpContext
  }
): Observable<Array<TextMessageTemplate>> {

    return this.listTextMessageTemplates$Response(params).pipe(
      map((r: StrictHttpResponse<Array<TextMessageTemplate>>) => r.body as Array<TextMessageTemplate>)
    );
  }

  /**
   * Path part for operation createTextMessageTemplate
   */
  static readonly CreateTextMessageTemplatePath = '/text-message-templates';

  /**
   * Create a new text message template.
   *
   * Requires the `templates:manage` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createTextMessageTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTextMessageTemplate$Response(params: {
    context?: HttpContext
    body: CreateTextMessageTemplateRequest
  }
): Observable<StrictHttpResponse<TextMessageTemplate>> {

    const rb = new RequestBuilder(this.rootUrl, TextMessageTemplatesService.CreateTextMessageTemplatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<TextMessageTemplate>;
      })
    );
  }

  /**
   * Create a new text message template.
   *
   * Requires the `templates:manage` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createTextMessageTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTextMessageTemplate(params: {
    context?: HttpContext
    body: CreateTextMessageTemplateRequest
  }
): Observable<TextMessageTemplate> {

    return this.createTextMessageTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<TextMessageTemplate>) => r.body as TextMessageTemplate)
    );
  }

  /**
   * Path part for operation updateTextMessageTemplate
   */
  static readonly UpdateTextMessageTemplatePath = '/text-message-templates/{templateId}';

  /**
   * Update an existing text message template.
   *
   * Requires the `templates:manage` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateTextMessageTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTextMessageTemplate$Response(params: {
    templateId: number;
    context?: HttpContext
    body: UpdateTextMessageTemplateRequest
  }
): Observable<StrictHttpResponse<TextMessageTemplate>> {

    const rb = new RequestBuilder(this.rootUrl, TextMessageTemplatesService.UpdateTextMessageTemplatePath, 'put');
    if (params) {
      rb.path('templateId', params.templateId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<TextMessageTemplate>;
      })
    );
  }

  /**
   * Update an existing text message template.
   *
   * Requires the `templates:manage` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateTextMessageTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTextMessageTemplate(params: {
    templateId: number;
    context?: HttpContext
    body: UpdateTextMessageTemplateRequest
  }
): Observable<TextMessageTemplate> {

    return this.updateTextMessageTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<TextMessageTemplate>) => r.body as TextMessageTemplate)
    );
  }

  /**
   * Path part for operation renderTextMessageTemplate
   */
  static readonly RenderTextMessageTemplatePath = '/text-message-templates/render';

  /**
   * Render a text message template for an invoice.
   *
   * Renders a text message template with data from a specific invoice to generate a preview of the final message content.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `renderTextMessageTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  renderTextMessageTemplate$Response(params: {
    context?: HttpContext
    body: RenderTextMessageTemplateRequest
  }
): Observable<StrictHttpResponse<RenderedTextMessageContent>> {

    const rb = new RequestBuilder(this.rootUrl, TextMessageTemplatesService.RenderTextMessageTemplatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RenderedTextMessageContent>;
      })
    );
  }

  /**
   * Render a text message template for an invoice.
   *
   * Renders a text message template with data from a specific invoice to generate a preview of the final message content.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `renderTextMessageTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  renderTextMessageTemplate(params: {
    context?: HttpContext
    body: RenderTextMessageTemplateRequest
  }
): Observable<RenderedTextMessageContent> {

    return this.renderTextMessageTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<RenderedTextMessageContent>) => r.body as RenderedTextMessageContent)
    );
  }

}
